import { MetricCard } from "@/modules/shared/components/metric-card";
import { Store, TrendingUp, TrendingDown, DollarSign, UserCheck, ArrowDownRight, ArrowUpRight, Activity } from "lucide-react";
import { PlatformOverview } from "@/services/api/services/admin-dashboard";
import { formatCurrency } from "@/services/api/services/admin-dashboard";

interface AdminPlatformOverviewProps {
  data: PlatformOverview;
  loading?: boolean;
}

export function AdminPlatformOverview({ data, loading = false }: AdminPlatformOverviewProps) {
  if (loading || !data) {
    return (
      <div className="space-y-6">
        {/* First Row Loading Skeletons */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={`row1-${i}`} className="p-4 sm:p-6 border rounded-lg">
              <div className="space-y-3">
                <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                <div className="h-8 w-32 bg-muted rounded animate-pulse" />
                <div className="h-3 w-20 bg-muted rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>

        {/* Second Row Loading Skeletons */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={`row2-${i}`} className="p-4 sm:p-6 border rounded-lg">
              <div className="space-y-3">
                <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                <div className="h-8 w-32 bg-muted rounded animate-pulse" />
                <div className="h-3 w-20 bg-muted rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* First Row - 3 Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <MetricCard
          title="Merchants"
          value={`${data.activeMerchants.toLocaleString()} active`}
          change={`${data.pendingMerchants} pending • ${data.suspendedMerchants} suspended`}
          changeType="neutral"
          icon={Store}
          iconColor="text-primary"
        />

        <MetricCard
          title="Platform Volume (90 days)"
          value={formatCurrency(data.totalPlatformVolume)}
          change="Total transaction volume last 90 days"
          changeType="neutral"
          icon={DollarSign}
          iconColor="text-warning"
        />

        <MetricCard
          title="Success Rate (90 days)"
          value={`${data.platformSuccessRate.toFixed(2)}%`}
          change="Platform transaction success rate"
          changeType={data.platformSuccessRate >= 95 ? "positive" : data.platformSuccessRate >= 90 ? "neutral" : "negative"}
          icon={data.platformSuccessRate >= 90 ? TrendingUp : TrendingDown}
          iconColor={data.platformSuccessRate >= 95 ? "text-success" : data.platformSuccessRate >= 90 ? "text-warning" : "text-destructive"}
        />
      </div>

      {/* Second Row - 4 Real-time Cards */}
      <div className="space-y-4">
        <h4 className="text-lg font-medium">Today's Real-time Metrics</h4>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <MetricCard
            title="Daily Revenue (fees)"
            value={formatCurrency(data.todayPayinVolume * 0.02)} // Assuming 2% fee
            change="Platform earnings from fees today"
            changeType="positive"
            icon={DollarSign}
            iconColor="text-success"
          />

          <MetricCard
            title="Total Collection (Payin)"
            value={formatCurrency(data.todayPayinVolume)}
            change={`${data.todayPayinCount} transactions today`}
            changeType="positive"
            icon={ArrowDownRight}
            iconColor="text-success"
          />

          <MetricCard
            title="Total Payment (Payout)"
            value={formatCurrency(data.todayPayoutVolume)}
            change={`${data.todayPayoutCount} payouts today`}
            changeType="neutral"
            icon={ArrowUpRight}
            iconColor="text-orange-600"
          />

          <MetricCard
            title="Today Volume"
            value={formatCurrency(data.todayTotalVolume)} // Using totalVolume from API response
            change={`${data.todaySuccessRate.toFixed(1)}% success rate`}
            changeType={data.todaySuccessRate >= 95 ? "positive" : data.todaySuccessRate >= 80 ? "neutral" : "negative"}
            icon={Activity}
            iconColor="text-purple-600"
          />
        </div>
      </div>
    </div>
  );
}